import { MobileWaage } from './waage';
import { Logger } from '../helper/app-error-logger';
import { BluetoothSerial } from '@awesome-cordova-plugins/bluetooth-serial/ngx';
import { MyApp } from '../helper/app';
import { Subject, firstValueFrom, timeout } from 'rxjs';
import { GeraetStatusTyp, WaageProtokollEintrag, WiegedatenEx } from '../model/model';
import { Utils } from '../helper/utils';
import { AppStorage } from '../helper/app-storage';
import { Wiegedaten } from '../model/swagger-model';

import moment from 'moment';
import { WaageBase } from './waage-base';

const BOARDCOMPUTER = 5;
const WAAGE = 1;

const log = new Logger('WelvaartsWaage');

export class WelvaartsWaage extends WaageBase implements MobileWaage {

    /**
     * Wird ausgelöst, wenn Code 34 von der Waage empfangen wird, als bestätigung auf Code 33.
     */
    ackWiegemodusAktiviert = new Subject<string>();

    /**
     * Acknowledge by Bcom of receive request 1e weighing.
     * Das ist nur die Bestätigung, dass die Waage die Wiegeanfrage erhalten hast. 
     */
    ackWiegen1Empfangen = new Subject<string>();

    ackWiegen2Empfangen = new Subject<string>();

    constructor(protected bluetoothSerial: BluetoothSerial) {
        super(log, bluetoothSerial);
    }

    async dispose(): Promise<void> {
        await super.dispose();

        Utils.removeAllSubscribers(
            this.ackWiegemodusAktiviert,
            this.ackWiegen1Empfangen,
            this.ackWiegen2Empfangen
        );
    }

    getTyp() {
        return 'welvaarts';
    }

    info1?() {
        log.warn('info1: Not implemented');
    }

    info2?() {
        log.warn('info2: Not implemented');
    }

    info3?() {
        log.warn('info3: Not implemented');
    }

    stabilIndikatorAbfragen?() {
        log.warn('stabilIndikatorAbfragen: Not implemented');
    }

    aufNullSetzen?() {
        log.warn('aufNullSetzen: Not implemented');
    }

    schreibeAlibieintrag?() {
        log.warn('schreibeAlibieintrag: Not implemented');
    }

    async leseLetzteAlibiNummer?() {
        log.warn('leseAlibieintrag: Not implemented');
        return 0;
    }

    waegedatenAbfragen?() {
        log.warn('waegedatenAbfragen: Not implemented');
    }

    async onRequestStableWeightInfo?() {
        log.debug('onRequestStableWeightInfo');

        try {
            await this.pruefeVerbindung();

            await this.senden('1#111#5#5#1#');
        } catch (err) {
            log.error('wiegen: ' + Utils.getErrorMessage(err), err);
        }
    }

    async onRequestWeightInfo?() {
        log.debug('onRequestWeightInfo');

        try {
            await this.pruefeVerbindung();

            await this.senden('1#113#5#1#');
        } catch (err) {
            log.error('wiegen: ' + Utils.getErrorMessage(err), err);
        }
    }

    aktiviereKontinuierlichesSendenDerWaegedaten?() {
        log.warn('aktiviereKontinuierlichesSendenDerWaegedaten: Not implemented');
    }

    async leseAnzahlGespeicherteWiegungen(): Promise<number> {
        log.warn('leseAnzahlGespeicherteWiegungen: Not implemented');
        return 0;
    }

    async einzelwiegung(): Promise<WiegedatenEx> {
        throw new Error('WELVAARTS: Einzelwiegung nicht verfügbar.');
    }

    async aktiviereWiegemodus(): Promise<boolean> {
        log?.debug('aktiviereWiegemodus');

        try {
            const data1 = '00000000'; // 16 Zeichen, z.B. Customer ID / Transaction Code
            const data2 = ''; // 10 Zeichen, KM-Stand
            const data3 = ''; // 10 Zeichen, Start time
            const data4 = ''; // 10 Zeichen, GPS X
            const data5 = ''; // 10 Zeichen, GPS Y

            const daten = `1#33#5#${data1}#${data2}#${data3}#${data4}#${data5}#`;

            await this.senden(daten);

            if (!MyApp.isCordovaAvailable()) {
                // Simulation. Nach 2 Sekunden so tun, als ob die Waage den Wiegemodus aktiviert hätte.
                setTimeout(() => {
                    this.onDataReceived('{5#34#1#00000000#@217}');
                }, 2000);
            }

            // Warte auf Antwort
            await firstValueFrom(this.ackWiegemodusAktiviert.pipe(timeout(10_000)));

            return true;
        } catch (err) {
            // Wird bei Timeout oder anderen Fehlern ausgelöst
            log.warn('aktiviereWiegemodus: ' + Utils.getErrorMessage(err), err);

            return false;
        }
    }

    async wiegen(nr: number): Promise<boolean> {
        log?.debug('wiegen: ' + nr);

        try {
            await this.pruefeVerbindung();

            if (nr == 1) {
                // Erste Wiegung
                await this.senden('1#51#5#');

                if (!MyApp.isCordovaAvailable()) {
                    setTimeout(() => {
                        this.onDataReceived('{5#52#1#@54}');
                    }, 2000);
                }

                // Warte auf das ACK von der Waage.
                // Das ist aber nur die Bestätigung, dass die Waage die Wiegeanfrage erhalten hat. 
                await firstValueFrom(this.ackWiegen1Empfangen.pipe(timeout(5_000)));
            } else if (nr == 2) {
                // Zweite Wiegung
                await this.senden('1#53#5#');

                if (!MyApp.isCordovaAvailable()) {
                    setTimeout(() => {
                        this.onDataReceived('{5#54#1#@0}');
                    }, 2000);
                }

                // Warte auf das ACK von der Waage.
                // Das ist aber nur die Bestätigung, dass die Waage die Wiegeanfrage erhalten hat. 
                await firstValueFrom(this.ackWiegen2Empfangen.pipe(timeout(5_000)));
            }

            return true;
        } catch (err) {
            log.warn('wiegen: ' + Utils.getErrorMessage(err), err);
            return false;
        }
    }

    async tarieren() {
        log?.debug('tarieren');

        try {
            await this.pruefeVerbindung();
            await this.senden('1#101#5#1#');
        } catch (err) {
            log.error('tarieren: ' + Utils.getErrorMessage(err), err);
        }
    }

    async speichereProtokoll() {
        // Zu alte Einträge löschen
        const maxEintraege = 100;
        const protokoll = await this.getProtokoll();

        if (protokoll.eintraege.length > maxEintraege) {
            protokoll.eintraege.splice(0, protokoll.eintraege.length - maxEintraege);
        }

        AppStorage.current.set('waage-protokoll-' + this.adresse, protokoll);

        this.protokollChanged.next();
    }

    getDataDelimiter() {
        return '}';
    }

    async onDataReceived(message: string) {
        try {
            log?.debug('Waage onDataReceived: ' + message);

            this.status.next(GeraetStatusTyp.Verbunden);
            this.datenEmpfangen.next();

            message = message.trim();

            if (!message.startsWith('{')) {
                // Verwerfe Nachricht, da nicht vollständig
                return;
            }

            const protokoll = await this.getProtokoll();

            const protokollEintrag: WaageProtokollEintrag = {
                timestamp: Date.now(),
                rohdaten: message,
                typ: 'unbekannt',
                daten: ''
            };

            protokoll.eintraege.push(protokollEintrag);

            await this.speichereProtokoll();

            // Wiededaten Beispiel:
            // {5#30#1#1915486214#08#02#20#12#57#50##VIE-EN xxx#####110##110##0#######@99}

            // Entferne die geschweiften Klammern
            const daten = message.substr(1, message.length - 2);

            log?.debug('Daten: ' + daten);

            const posAt = daten.indexOf('@');

            if (posAt <= 0) {
                // Keien Checksumme gefunden. Ignorieren
                log?.debug('Keine Checksumme gefunden. Ignoriere Nachricht.');
                return;
            }

            const nutzdaten = daten.substr(0, posAt);
            const checksumme = parseInt(daten.substr(posAt + 1), 10);
            const berechneteChecksum = this.berechneChecksumme(nutzdaten);

            log?.debug('nutzdaten: ' + nutzdaten);
            log?.debug('checksumme: ' + checksumme);
            log?.debug('berechneteChecksum: ' + berechneteChecksum);

            if (checksumme != berechneteChecksum) {
                log.warn('Checksumme ungültig. Egal.');
                // return;
            }

            const parts = daten.split('#');

            const empfaenger = parseInt(parts[0], 10);
            const command = parseInt(parts[1], 10);
            const absender = parseInt(parts[2], 10);

            if (empfaenger != BOARDCOMPUTER) {
                // 5 ist der Boardcomputer, also wir.
                // Alles für andere Empfänger ignorieren
                log.warn('Ungültiger Empfänger: ' + empfaenger);
                return;
            }

            if (absender != WAAGE) {
                log.warn('Ungültiger Absender: ' + absender);
                return;
            }

            switch (command) {
                case 1:
                    protokollEintrag.daten = 'NACK_CHECKSUM';
                    protokollEintrag.typ = 'NACK';
                    break;

                case 3:
                    protokollEintrag.daten = 'NACK_VARIABLE_COUNT: ' + parts[3] + ' ' + parts[4];
                    protokollEintrag.typ = 'NACK';
                    break;

                case 5:
                    protokollEintrag.daten = 'NACK_UNKNOWN_COMMAND';
                    protokollEintrag.typ = 'NACK';
                    break;

                case 9:
                    protokollEintrag.daten = 'NACK_GENERAL: ' + parts[3];
                    protokollEintrag.typ = 'NACK';
                    break;

                case 30:
                    this.verarbeiteWiegedaten(parts.slice(3), protokollEintrag);
                    break;

                case 34:
                    // Acknowledge that arrival data is received
                    protokollEintrag.daten = 'ACK ARRIVAL: ' + parts[3];
                    protokollEintrag.typ = 'ACK';

                    this.ackWiegemodusAktiviert.next(message)
                    break;

                case 14:
                    log?.info('Acknowledge synchronise clock empfangen');
                    protokollEintrag.daten = 'ACK CLOCK SYNC';
                    protokollEintrag.typ = 'ACK';
                    break;

                case 52:
                    protokollEintrag.daten = 'ACK WEIGHING 1 REQ: ' + parts[3];
                    protokollEintrag.typ = 'ACK';
                    this.ackWiegen1Empfangen.next(message);
                    break;

                case 54:
                    protokollEintrag.daten = 'ACK WEIGHING 2 REQ: ' + parts[3];
                    protokollEintrag.typ = 'ACK';
                    this.ackWiegen2Empfangen.next(message);
                    break;

                case 102:
                    protokollEintrag.daten = 'ACK RESET TARE REQ: ' + parts[3];
                    protokollEintrag.typ = 'ACK';
                    break;

                case 112:
                    // Reply on Stable weight request
                    protokollEintrag.daten = protokollEintrag.rohdaten;
                    protokollEintrag.typ = 'Daten';
                    break;

                case 114:
                    // Reply on weight request
                    protokollEintrag.daten = protokollEintrag.rohdaten;
                    protokollEintrag.typ = 'Daten';
                    break;

                default:
                    protokollEintrag.daten = "Unbekanntes Command: " + command + '. Rohdaten: ' + protokollEintrag.rohdaten;
                    protokollEintrag.typ = 'unbekannt';
            }

        } catch (err) {
            log.error('onDataReceived', err);
            this.error.next(Utils.getErrorMessage(err));
        }
    }

    async verarbeiteWiegedaten(data: string[], protokollEintrag: WaageProtokollEintrag) {
        const serialNumber = parseInt(data[0], 10);
        const day = parseInt(data[1], 10);
        const month = parseInt(data[2], 10);
        const year = parseInt(data[3], 10);
        const hour = parseInt(data[4], 10);
        const minute = parseInt(data[5], 10);
        const second = parseInt(data[6], 10);
        // const customerId = data[7];
        // const licensePlateTruck = data[8];
        // const gps = data[9];
        // const rfid = data[10];
        // const statusCode = parseInt(data[11], 10);
        // const wasteCode = parseInt(data[12], 10);
        const weight1e = parseFloat(data[13]);
        // const status1e = data[14];
        const weight2e = parseFloat(data[15]);
        // const status2e = data[16];
        const weightContents = parseFloat(data[17]);

        // Achtung: Das Datum muss in einem Rutsch gesetzt werden.
        // Ein einzelnes .day(...) setzt den Wochentag und nicht den Tag im Monat,
        // und beim schrittweisen Setzen kann der Monatswechsel überlaufen (z.B. 31. -> Folgemonat).
        const datum = moment({
            year: 2000 + year,
            month: month - 1, // Januar = 0
            date: day,
            hour: hour,
            minute: minute,
            second: second
        });

        const wiegedaten: Wiegedaten = {
            Wiegenummer: serialNumber,
            Datum: datum.toISOString(),
            Wiegung1: weight1e,
            Wiegung2: weight2e,
            Netto: weightContents
        };

        log?.info('Wiegedaten gelesen', wiegedaten);

        // Wiegung speichern
        // TODO
        this.wiegedatenEmpfangen.next(wiegedaten);

        // Antworten
        await this.senden(`1#31#5#${serialNumber}#`);

        protokollEintrag.daten = `Nr. ${serialNumber}, Datum: ${datum.format('DD.MM.YYYY HH:mm:ss')}, W1: ${weight1e}, W2: ${weight2e}, NET: ${wiegedaten.Netto}`;
        protokollEintrag.typ = 'wiegung';

        await this.speichereProtokoll();
    }

    async syncUhr(pruefeVerbindung = true) {
        log?.debug('syncUhr');

        try {
            if (pruefeVerbindung) {
                await this.pruefeVerbindung();
            }

            const now = moment();

            const daten = '1#13#5'
                + '#' + now.date()
                + '#' + (now.month() + 1)
                + '#' + (now.year() - 2000)
                + '#' + now.hour()
                + '#' + now.minute()
                + '#' + now.second()
                + '#';

            await this.senden(daten);
        } catch (err) {
            log.error('syncUhr: ' + Utils.getErrorMessage(err), err);
        }
    }

    async senden(nutzdaten: string) {
        const checksumme = this.berechneChecksumme(nutzdaten);
        const message = "{" + nutzdaten + "@" + checksumme.toString() + '}';

        log?.debug('senden: ' + message);

        const protokoll = await this.getProtokoll();

        protokoll.eintraege.push({
            daten: 'SEND: ' + message,
            typ: 'senden',
            timestamp: Date.now(),
            rohdaten: message
        });

        if (MyApp.isCordovaAvailable()) {
            await this.bluetoothSerial.write(message);
        } else {
            // Simulation.
            await Utils.delay(10);
        }
    }

    berechneChecksumme(nutzdaten: string): number {
        let checksum = 0;

        for (const c of nutzdaten) {
            checksum += c.charCodeAt(0);
        }

        return checksum % 256;
    }

    simuliereWiegedatenEmpfaengen() {
        log?.debug('simuliereWiegedatenEmpfaengen');

        this.onDataReceived('{5#30#1#1915486214#08#02#20#12#57#50##VIE-EN xxx#####710##110##600#######@99}');
    }
}
